import React, { useEffect, useRef, useState } from 'react';
import { View } from 'react-native';
import { Button, Icon, Text, TextInput, useTheme } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '@/src/api/apiService';
import { API_BASE } from '@/src/api/config';
import { isReferralCodeShape, normalizeReferralCode } from './offerCatalogue';
import { isReferralLinkExpired, isRegistrationSessionRejection } from './registrationReferral';

export type ReferralState =
  | { kind: 'idle' }
  | { kind: 'pending' }
  | { kind: 'declined' }
  | { kind: 'loading' }
  | { kind: 'valid'; partnerName: string; code: string; linkCapturedAt?: string }
  | { kind: 'invalid' }
  | { kind: 'transient' };

interface Props {
  /**
   * SERVER-issued registration session token (see `beginRegistrationSession`).
   * Never invented client-side; `null` means the session endpoint could not be
   * reached, which is reported as a transient state rather than faked.
   */
  registrationToken: string | null;
  onRegistrationRetry?: () => Promise<string | null>;
  initialCode?: string;
  /**
   * When `initialCode` came from a link/click kept on the device: its capture
   * time. Sent while the code is untouched so the server applies the Admin link
   * validity (D-057); a code the person edits or types is an explicit entry.
   */
  linkCapturedAt?: string;
  /** Completion is authoritative even when public code eligibility succeeds. */
  serverRejected?: string;
  onDraftChange?: (code: string) => void;
  onChange?: (state: ReferralState) => void;
  /**
   * Incremented by the parent after it replaced a session that expired at
   * completion (F10): an already accepted code is re-captured into the new one.
   */
  recaptureSignal?: number;
}

/**
 * Buyer-side referral code entry during SIGNUP (D-057).
 *
 * The code is validated against the public `/validate-code/` endpoint with the
 * SERVER-issued `registration_token`; a valid code becomes the last-valid
 * candidate for that session and is frozen atomically by the backend inside the
 * signup transaction (the signup request itself carries `referral_code` /
 * `referral_token` — there is no post-signup claim). Used only in registration
 * flows, never in an existing account's checkout.
 */
export function ReferralCodeField({ registrationToken, onRegistrationRetry, initialCode = '', linkCapturedAt, serverRejected, onChange, onDraftChange, recaptureSignal = 0 }: Props) {
  const { t } = useTranslation('partner');
  const theme = useTheme();
  const [code, setCode] = useState(initialCode);
  const [state, setState] = useState<ReferralState>({ kind: serverRejected ? 'invalid' : initialCode ? 'pending' : 'idle' });
  const inFlight = useRef(false);
  const generation = useRef(0);
  const tokenRef = useRef(registrationToken);
  tokenRef.current = registrationToken;
  const previousToken = useRef(registrationToken);
  const recovering = useRef(false);
  // Session (possibly none) this field obtained itself while recovering.
  const selfIssued = useRef<{ token: string | null } | null>(null);
  const lastValidCode = useRef<string | null>(null);
  // The link capture applies only to the untouched link code (D-057).
  const linkUntouched = useRef(!!linkCapturedAt);
  const [linkExpired, setLinkExpired] = useState(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; generation.current += 1; };
  }, []);

  const emit = (next: ReferralState) => {
    if (!mounted.current) return;
    if (next.kind === 'valid') lastValidCode.current = next.code;
    else if (next.kind === 'invalid' || next.kind === 'declined') lastValidCode.current = null;
    setState(next); onChange?.(next);
  };

  const validate = async (recoverSession = false) => {
    if (serverRejected) { emit({ kind: 'invalid' }); return; }
    if (inFlight.current) return;
    const normalized = normalizeReferralCode(code);
    if (!isReferralCodeShape(normalized)) { emit({ kind: 'invalid' }); return; }
    inFlight.current = true;
    const linkAt = linkUntouched.current && linkCapturedAt && normalized === normalizeReferralCode(initialCode)
      ? linkCapturedAt : undefined;
    const requestGeneration = ++generation.current;
    const originalToken = registrationToken;
    // Tokens this request may legitimately observe: its own, plus a fresh
    // session it requested itself before the parent re-renders with it.
    const accepted = new Set<string | null>([originalToken]);
    const current = () => mounted.current && generation.current === requestGeneration
      && (accepted.has(tokenRef.current) || (recoverSession && !originalToken && !tokenRef.current));
    const check = (token: string) => apiFetch<{ valid?: boolean; partner_name?: string }>(
      `${API_BASE}/api/referrals/validate-code/`,
      {
        method: 'POST', includeCompanyContext: false,
        body: JSON.stringify({ registration_token: token, code: normalized, ...(linkAt ? { link_captured_at: linkAt } : {}) }),
      },
      false, false,
    );
    try {
      emit({ kind: 'loading' });
      const token = registrationToken || (recoverSession ? await onRegistrationRetry?.() : null);
      accepted.add(token ?? null);
      if (!current()) return;
      if (!token) { emit({ kind: 'transient' }); return; }
      let response = await check(token);
      if (!current()) return;
      if (!response.ok && isRegistrationSessionRejection(response.data)) {
        // F10: an expired/unknown session is not an invalid code. Recover once
        // with a fresh server session and re-capture the same code.
        recovering.current = true;
        const fresh = onRegistrationRetry ? await onRegistrationRetry() : null;
        selfIssued.current = { token: fresh ?? null };
        accepted.add(fresh);
        if (!current()) return;
        if (!fresh) { emit({ kind: 'transient' }); return; }
        response = await check(fresh);
        if (!current()) return;
        if (!response.ok && isRegistrationSessionRejection(response.data)) { emit({ kind: 'transient' }); return; }
      }
      if (response.ok && response.data?.valid) {
        emit({ kind: 'valid', partnerName: response.data.partner_name || '', code: normalized, ...(linkAt ? { linkCapturedAt: linkAt } : {}) });
      } else if (!response.ok && isReferralLinkExpired(response.data)) {
        linkUntouched.current = false;
        setLinkExpired(true);
        emit({ kind: 'invalid' });
      } else if (response.status === 0 || response.status === 429 || response.status >= 500) {
        emit({ kind: 'transient' });
      } else {
        emit({ kind: 'invalid' });
      }
    } catch {
      if (current()) emit({ kind: 'transient' });
    } finally {
      if (generation.current === requestGeneration) {
        inFlight.current = false;
        recovering.current = false;
        // Already adopted by the parent while recovering: nothing left to skip.
        if (selfIssued.current && selfIssued.current.token === tokenRef.current) selfIssued.current = null;
      }
    }
  };

  // A code carried by a referral link is validated once, as soon as the server
  // session exists, so link attribution never depends on an extra tap. Only the
  // untouched link code is auto-validated; manual entry still needs the button.
  const autoValidated = useRef(false);
  useEffect(() => {
    if (!serverRejected) return;
    generation.current += 1;
    inFlight.current = false;
    autoValidated.current = true;
    emit({ kind: 'invalid' });
    // Parent clears the rejection only on explicit correction or opt-out.
  }, [serverRejected]);
  useEffect(() => {
    if (serverRejected) return;
    // A session this field requested while recovering an expiry is its own.
    if (recovering.current || (selfIssued.current && selfIssued.current.token === registrationToken)) {
      if (!recovering.current) selfIssued.current = null;
      previousToken.current = registrationToken;
      return;
    }
    if (previousToken.current && previousToken.current !== registrationToken) {
      generation.current += 1;
      inFlight.current = false;
      emit({ kind: code ? 'pending' : 'idle' });
    }
    previousToken.current = registrationToken;
    if (autoValidated.current || !registrationToken) return;
    autoValidated.current = true;
    if ((state.kind === 'idle' || state.kind === 'pending') && isReferralCodeShape(initialCode)
      && normalizeReferralCode(code) === normalizeReferralCode(initialCode)) {
      void validate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registrationToken]);

  // Declared after the session effect so a replaced session is adopted first.
  useEffect(() => {
    if (!recaptureSignal || serverRejected) return;
    // F10: only a code the user had already accepted is re-captured; a draft
    // that was never validated still needs an explicit tap.
    if (lastValidCode.current && lastValidCode.current === normalizeReferralCode(code)) void validate();
  }, [recaptureSignal]);

  const edit = (value: string) => {
    generation.current += 1;
    inFlight.current = false;
    recovering.current = false;
    linkUntouched.current = false;
    setLinkExpired(false);
    setCode(value);
    onDraftChange?.(value);
    emit({ kind: 'pending' });
  };
  const decline = () => {
    generation.current += 1;
    inFlight.current = false;
    recovering.current = false;
    setCode('');
    onDraftChange?.('');
    emit({ kind: 'declined' });
  };
  const loading = state.kind === 'loading';

  return <View style={{ gap: 8 }}>
    <TextInput
      label={t('codeFieldLabel')} accessibilityLabel={t('codeFieldLabel')} value={code}
      onChangeText={edit} autoCapitalize="characters" editable
      testID="referral-code-input"
    />
    <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>{t('codeFieldHelp')}</Text>
    {state.kind === 'valid' && <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
      <Icon source="check-circle" size={20} color={theme.colors.primary} />
      <Text testID="referral-code-valid" style={{ flexShrink: 1 }}>{t('codeFieldValid', { partner: state.partnerName })}</Text>
    </View>}
    {state.kind === 'invalid' && <View style={{ gap: 8 }}>
      <Text accessibilityRole="alert" testID="referral-code-invalid" style={{ color: theme.colors.error }}>{serverRejected || (linkExpired ? t('codeFieldLinkExpired') : t('codeFieldInvalid'))}</Text>
      <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
        <Button contentStyle={{ minHeight: 44 }} onPress={() => edit(code)}>{t('codeFieldCorrect')}</Button>
      </View>
    </View>}
    {state.kind === 'transient' && <View style={{ gap: 8 }}>
      <Text accessibilityRole="alert" testID="referral-code-transient" style={{ color: theme.colors.error }}>{t('codeFieldTransient')}</Text>
      <Button contentStyle={{ minHeight: 44 }} disabled={loading || (!registrationToken && !onRegistrationRetry)} onPress={() => void validate(true)}>{t('retry')}</Button>
      <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>{t('codeFieldTransientWarn')}</Text>
    </View>}
    {state.kind !== 'valid' && <Button mode="contained" contentStyle={{ minHeight: 44 }} loading={loading} disabled={loading || !code.trim()}
      testID="referral-code-validate" onPress={() => void validate()}>{t('codeFieldValidate')}</Button>}
    {state.kind !== 'idle' && state.kind !== 'declined' && <View style={{ gap: 8 }}>
      <Text testID="referral-code-optout-warning" variant="bodySmall">{t('codeFieldTransientWarn')}</Text>
      <Button testID="referral-code-decline" contentStyle={{ minHeight: 44 }} onPress={decline}>{t('codeFieldContinueWithout')}</Button>
    </View>}
  </View>;
}

export default ReferralCodeField;
